<template>
  <van-popup v-model="innerVisible" round position="bottom" :style="{ height: '80%' }">
    <div style="padding: 10px 12px 0 12px">
      <!-- 搜索框 -->
      <div style="display: flex; align-items: center; margin-bottom: 8px">
        <input
          v-model="mapSearchKeyword"
          type="text"
          placeholder="搜索地点，例如：某某商场、某某小区"
          style="flex: 1; height: 32px; line-height: 32px; border: 1px solid #ddd; border-radius: 4px; padding: 0 8px; font-size: 12px"
        />
        <button
          type="button"
          @click="searchPlace"
          style="margin-left: 8px; height: 32px; padding: 0 10px; border: none; background: #333333; color: #ffebc4; font-size: 12px"
        >
          搜索
        </button>
      </div>

      <!-- 地图 -->
      <div :id="mapContainerId" style="width: 100%; height: 260px"></div>

      <!-- 搜索结果列表 -->
      <ul v-if="searchResults.length" style="margin-top: 8px; max-height: 240px; overflow-y: auto; padding: 0 4px">
        <li
          v-for="item in searchResults"
          :key="item.id"
          @click="chooseSearchResult(item)"
          style="padding: 6px 4px; border-bottom: 1px solid #eee; font-size: 12px; cursor: pointer"
        >
          <div style="color: #333; font-weight: 500">{{ item.name }}</div>
          <div style="color: #666; margin-top: 2px">{{ item.address }}</div>
        </li>
      </ul>

      <!-- 当前选中 -->
      <p v-if="tempAddress" style="margin-top: 6px; font-size: 12px; color: #666">
        当前选中：{{ tempAddress }}（{{ tempLng }}, {{ tempLat }}）
      </p>

      <!-- 底部按钮 -->
      <div style="text-align: right; margin-top: 10px">
        <button
          type="button"
          @click="handleCancel"
          style="margin-right: 10px; height: 32px; padding: 0 14px; border: 1px solid #ddd; background: #fff; font-size: 12px"
        >
          取消
        </button>
        <button
          type="button"
          @click="handleConfirmClick"
          style="height: 32px; padding: 0 14px; border: none; background: #333333; color: #ffebc4; font-size: 12px"
        >
          确定
        </button>
      </div>
    </div>
  </van-popup>
</template>

<script>
import { Popup, Toast } from 'vant';

const AMAP_WEB_KEY = 'abe69f24bf9e8f9fa524e6733fb998cc'; // 你的 Web 服务 key

export default {
  name: 'MapSelector',
  components: {
    [Popup.name]: Popup
  },
  props: {
    // v-model:visible
    visible: {
      type: Boolean,
      default: false
    },
    // 省市区文本，例如 “辽宁省沈阳市和平区”，用于初始化地图中心
    regionText: {
      type: String,
      default: ''
    },
    // 历史经纬度（可选）
    initialLng: {
      type: [String, Number],
      default: ''
    },
    initialLat: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      innerVisible: this.visible,
      mapInstance: null,
      mapMarker: null,
      mapSearchKeyword: '',
      searchResults: [],
      tempLng: '',
      tempLat: '',
      tempAddress: '',
      mapContainerId: 'amapContainer_' + Math.random().toString(36).slice(2)
    };
  },
  watch: {
    visible(val) {
      console.log('MapSelector visible changed:', val);
      this.innerVisible = val;
      if (val) {
        this.$nextTick(() => {
          this.initMap();
        });
      }
    },
    innerVisible(val) {
      this.$emit('update:visible', val);
    }
  },
  mounted() {
    if (this.innerVisible) {
      this.initMap();
    }
  },
  methods: {
    initMap() {
      // eslint-disable-next-line no-undef
      if (!window.AMap) {
        console.error('AMap 未加载，请检查 index.html 中的高德 JS 引用');
        return;
      }

      if (!this.mapInstance) {
        // eslint-disable-next-line no-undef
        const map = new AMap.Map(this.mapContainerId, {
          zoom: 14,
          center: [104.06, 30.67]
        });
        this.mapInstance = map;

        // eslint-disable-next-line no-undef
        this.mapMarker = new AMap.Marker({
          offset: new AMap.Pixel(-12, -36)
        });
        this.mapInstance.add(this.mapMarker);

        // 点击选点
        map.on('click', (e) => {
          this.searchResults = [];
          const lng = e.lnglat.getLng();
          const lat = e.lnglat.getLat();
          this.tempLng = lng;
          this.tempLat = lat;

          if (this.mapMarker) {
            this.mapMarker.setPosition([lng, lat]);
          }

          // 调用 Web 服务逆地理编码
          const url =
            `https://restapi.amap.com/v3/geocode/regeo?` +
            `key=${AMAP_WEB_KEY}&location=${lng},${lat}&extensions=all&radius=50&batch=false`;

          fetch(url)
            .then((res) => res.json())
            .then((data) => {
              if (data.status === '1' && data.regeocode) {
                const fullAddress = data.regeocode.formatted_address || '';
                this.tempAddress = fullAddress;
              } else {
                console.warn('逆地理编码失败：', lng, lat, data);
                this.tempAddress = '';
              }
            })
            .catch((err) => {
              console.error('调用逆地理编码接口异常：', err);
            });
        });
      } else {
        this.mapInstance.resize();
      }

      // 历史坐标
      if (this.initialLng && this.initialLat) {
        const lng = Number(this.initialLng);
        const lat = Number(this.initialLat);
        if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
          this.mapInstance.setZoom(16);
          this.mapInstance.setCenter([lng, lat]);
          this.mapMarker && this.mapMarker.setPosition([lng, lat]);
          this.tempLng = lng;
          this.tempLat = lat;
        }
      }

      // 根据省市区初始化中心
      const region = (this.regionText || '').replace(/-/g, '').trim();
      if (region) {
        const url = `https://restapi.amap.com/v3/geocode/geo?key=${AMAP_WEB_KEY}&address=${encodeURIComponent(region)}`;
        fetch(url)
          .then((res) => res.json())
          .then((data) => {
            if (data.status === '1' && data.geocodes && data.geocodes.length > 0) {
              const locStr = data.geocodes[0].location;
              if (locStr) {
                const [lngStr, latStr] = locStr.split(',');
                const lng = Number(lngStr);
                const lat = Number(latStr);
                if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
                  this.mapInstance.setZoom(14);
                  this.mapInstance.setCenter([lng, lat]);
                  this.mapMarker && this.mapMarker.setPosition([lng, lat]);
                  this.tempLng = lng;
                  this.tempLat = lat;
                  this.tempAddress = data.geocodes[0].formatted_address || region;
                }
              }
            } else {
              console.warn('根据地区地理编码失败：', region, data);
            }
          })
          .catch((err) => {
            console.error('调用地理编码接口异常：', err);
          });
      }
    },

    searchPlace() {
      const keyword = (this.mapSearchKeyword || '').trim();
      if (!keyword) {
        Toast.fail('请输入搜索关键字');
        return;
      }

      const city = ''; // 如需按城市限制，可通过 props 传入

      const params = new URLSearchParams({
        key: AMAP_WEB_KEY,
        keywords: keyword,
        city,
        offset: '20',
        page: '1',
        extensions: 'all'
      });

      const url = `https://restapi.amap.com/v3/place/text?${params.toString()}`;

      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          if (data.status === '1' && data.pois && data.pois.length > 0) {
            this.searchResults = data.pois
              .filter((poi) => poi.location)
              .map((poi) => {
                const [lngStr, latStr] = poi.location.split(',');
                return {
                  id: poi.id,
                  name: poi.name,
                  address: poi.address || poi.adname || '',
                  lng: Number(lngStr),
                  lat: Number(latStr)
                };
              });
            if (!this.searchResults.length) {
              Toast.fail('未找到带坐标的地点，请尝试换一个关键字');
            }
          } else {
            this.searchResults = [];
            Toast.fail('未找到相关地点，请尝试换一个关键字');
          }
        })
        .catch((err) => {
          console.error('调用关键字搜索接口异常：', err);
          this.searchResults = [];
          Toast.fail('地点搜索失败，请稍后再试');
        });
    },

    chooseSearchResult(item) {
      const { lng, lat, name, address } = item;
      if (Number.isNaN(lng) || Number.isNaN(lat)) {
        Toast.fail('该地点坐标异常');
        return;
      }
      if (this.mapInstance) {
        this.mapInstance.setCenter([lng, lat]);
        this.mapInstance.setZoom(16);
      }
      this.mapMarker && this.mapMarker.setPosition([lng, lat]);
      const fullAddress = (name || '') + (address ? ' ' + address : '');
      this.tempLng = lng;
      this.tempLat = lat;
      this.tempAddress = fullAddress;
    },

    handleConfirmClick() {
      if (!this.tempLng || !this.tempLat) {
        Toast.fail('请先在地图上点击选择位置或搜索选择地点');
        return;
      }
      this.$emit('confirm', {
        lng: this.tempLng,
        lat: this.tempLat,
        address: this.tempAddress
      });
      this.innerVisible = false;
    },

    handleCancel() {
      this.$emit('cancel');
      this.innerVisible = false;
    }
  }
};
</script>
```


// WEBPACK FOOTER //
// src/components/MapSelector.vue